import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiPlus, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft, FiEye, FiFileText, FiPackage, FiCamera } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, LineTableContainer, LineTable, LineTh, LineTd, LineInput, RemoveLineBtn
} from '../../components/SharedStyles';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getItemsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import { useLocation } from 'react-router-dom';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import ItemSelect from '../../components/ItemSelect';
import POSCameraScanner from '../pos/POSCameraScanner';
import {
  createTransaction, deleteTransaction, updateTransaction
} from '../../utils/transactionHelper';
import { generateStockFlow, invalidateItemStock } from '../../utils/stockFlowHelper';
import { costRowsChange } from '../../utils/itemCostEngine';

const TRAN_TYPE_CODE = 'RCV';
const SUMMARY_SPEC = { statuses: ['Draft', 'Received'] };
const EMPTY_LINE = { itemId: '', itemCode: '', itemName: '', description: '', quantity: 1, unitOfMeasure: '', trackingType: 'None', batchNumber: '', serialNumbers: [] };
const STATUS_COLORS = { Draft: colors.warning.main, Received: colors.success.main, Cancelled: colors.error.main };

// INV-15: save the document and the rows `addRows` adds in ONE commit
// (transactionHelper runs it inside its own save).
const saveWithRows = async (save, addRows) => {
  // The helper always runs extraWrites inside its save. If it did not here,
  // this call was merged into a save already running (double click) — that
  // save added the rows, so nothing is added again.
  return save({ extraWrites: (w, ctx) => addRows(w, ctx) });
};

// INV-15: a new document keeps ONE pre-generated id until it is saved (`pend`),
// so a retry after a dropped connection can never save it twice — if the earlier
// attempt did reach the server, that saved document is returned instead.
const savedEarlier = async (companyId, pend) => {
  if (!pend.tried) { pend.tried = true; return null; }
  const snap = await getDoc(doc(db, 'companies', companyId, 'transactions', pend.id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

const ReceiveInventory = () => {
  const { activeCompany, currentUser, activeFinancialYear, activeBranch } = useAuth();
  const { canEdit, canDelete } = usePagePermissions('transactions', 'stockTransfer');
  const location = useLocation();
  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All' || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: records, loading, refresh: loadRecords, ensureDoc, applyLocal, summary: listSummary, pagination
  } = useDocListPagination({ tranTypeCode: TRAN_TYPE_CODE, fullMode: listFiltering, summarySpec: SUMMARY_SPEC });

  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [transferNo, setTransferNo] = useState('');
  const [send, setSend] = useState(null); // the linked Send Inventory doc (NEVER shown to receiver)
  const [lookupErr, setLookupErr] = useState('');
  const [looking, setLooking] = useState(false);
  const [docDate, setDocDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [items, setItems] = useState([]);
  const [scanValue, setScanValue] = useState('');
  const [scanMsg, setScanMsg] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const scanInputRef = useRef(null);
  const newDocRef = useRef(null); // INV-15: { id, tried } of the receive being saved

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (e) { console.error('Error:', e); }
  }, [activeCompany]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r => r.docNumber?.toLowerCase().includes(q) || r.parentDocNumber?.toLowerCase().includes(q) || r.toLocationName?.toLowerCase().includes(q));
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, sort]);

  // ── Look up the Send by its Transfer No, lock the destination, enforce branch ──
  const lookupSend = async () => {
    const num = transferNo.trim();
    if (!num) { setLookupErr('Enter the Transfer No given by the sender.'); return; }
    if (!activeCompany?.id) return;
    setLooking(true); setLookupErr('');
    try {
      const snap = await getDocs(query(collection(db, 'companies', activeCompany.id, 'transactions'), where('docNumber', '==', num)));
      const snd = snap.docs.map(d => ({ id: d.id, ...d.data() })).find(d => d.tranTypeCode === 'SND');
      if (!snd) { setLookupErr(`No Send Inventory found with number "${num}".`); setSend(null); return; }
      if (snd.status === 'Draft') { setLookupErr('That transfer has not been sent yet.'); setSend(null); return; }
      if (snd.status === 'Cancelled') { setLookupErr('That transfer was cancelled.'); setSend(null); return; }
      // Enforce destination branch: the receiver can only receive at the branch the sender chose.
      if (snd.toBranchId && activeBranch && activeBranch.id !== 'all' && activeBranch.id !== snd.toBranchId) {
        setLookupErr(`This transfer is addressed to branch "${snd.toBranchName || snd.toBranchId}". Switch to that branch to receive it.`);
        setSend(null); return;
      }
      setSend(snd);
      setLineItems([]);
      setScanMsg('Transfer found. Scan or add the items that physically arrived.');
    } catch (e) { console.error('lookup error', e); setLookupErr('Lookup failed: ' + e.message); }
    finally { setLooking(false); }
  };

  // Add/increment a received line by item (blind — never references the Send's lines).
  const addItem = useCallback((item, qty = 1) => {
    if (!item) return;
    setLineItems(prev => {
      const idx = prev.findIndex(l => l.itemId === item.id);
      if (idx >= 0 && (item.trackingType || 'None') !== 'Serial') {
        const u = [...prev]; u[idx] = { ...u[idx], quantity: (Number(u[idx].quantity) || 0) + qty }; return u;
      }
      return [...prev, {
        ...EMPTY_LINE, itemId: item.id, itemCode: item.itemCode || '', itemName: item.itemName || '',
        description: item.itemName || '', quantity: qty, unitOfMeasure: item.baseUnit || item.unitOfMeasure || 'Pcs',
        trackingType: item.trackingType || 'None', serialNumbers: []
      }];
    });
  }, []);

  const addByBarcode = useCallback((code) => {
    const c = (code || '').trim(); if (!c) return;
    const item = items.find(i => i.barcode === c || i.itemCode === c);
    if (!item) { setScanMsg(`Unknown barcode: ${c}`); return; }
    addItem(item, 1);
    setScanMsg(`Added ${item.itemName}`);
  }, [items, addItem]);

  // Scanning is focus-driven: a USB scanner types into whichever field is focused
  // (the item scan box → adds items; a serial field → adds that serial), so item
  // and serial scans never collide. Auto-focus the item box when a Send loads.
  useEffect(() => { if (send && scanInputRef.current) scanInputRef.current.focus(); }, [send]);

  const setLineQty = (index, qty) => setLineItems(prev => prev.map((l, i) => i === index ? { ...l, quantity: Math.max(0, Number(qty) || 0) } : l));
  const setLineBatch = (index, val) => setLineItems(prev => prev.map((l, i) => i === index ? { ...l, batchNumber: val } : l));
  const addSerial = (index, serial) => {
    const s = (serial || '').trim(); if (!s) return;
    setLineItems(prev => prev.map((l, i) => {
      if (i !== index) return l;
      if ((l.serialNumbers || []).includes(s)) return l;
      const serialNumbers = [...(l.serialNumbers || []), s];
      return { ...l, serialNumbers, quantity: serialNumbers.length };
    }));
  };
  const removeSerial = (index, serial) => setLineItems(prev => prev.map((l, i) => {
    if (i !== index) return l;
    const serialNumbers = (l.serialNumbers || []).filter(x => x !== serial);
    return { ...l, serialNumbers, quantity: serialNumbers.length };
  }));
  const removeLine = (index) => setLineItems(prev => prev.filter((_, i) => i !== index));

  const resetForm = () => {
    setTransferNo(''); setSend(null); setLookupErr(''); setLineItems([]);
    setDocDate(new Date().toISOString().split('T')[0]); setNotes(''); setScanValue(''); setScanMsg('');
    newDocRef.current = null;
  };
  const handleCreate = () => { resetForm(); setViewMode('form'); };
  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };

  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(rec => { if (rec) { setViewingRecord(rec); setViewMode('view'); window.history.replaceState({}, document.title); } });
  }, [location.state, ensureDoc]);

  const handleSave = async () => {
    if (!canEdit) { alert('You do not have permission to save.'); return; }
    if (!send) { alert('Look up a Transfer No first.'); return; }
    const validLines = lineItems.filter(l => l.itemId && Number(l.quantity) > 0);
    if (validLines.length === 0) { alert('Scan or add at least one item.'); return; }
    for (const l of validLines) {
      if (l.trackingType === 'Batch' && !l.batchNumber?.trim()) { alert(`Enter the batch number for "${l.itemName}".`); return; }
      if (l.trackingType === 'Serial' && (l.serialNumbers || []).length !== Number(l.quantity)) { alert(`"${l.itemName}" needs a serial for each unit (${l.quantity}).`); return; }
    }
    if (!activeFinancialYear?.id) { alert('No active financial year.'); return; }

    setSaving(true);
    try {
      // Inherit each item's cost from the Send (backend only — receiver never sees it).
      const sendCostByItem = {};
      (send.lines || []).forEach(sl => { if (sl.itemId) sendCostByItem[sl.itemId] = Number(sl.unitCost) || 0; });

      const calculatedLines = validLines.map((l, i) => {
        const item = items.find(it => it.id === l.itemId);
        const unitCost = sendCostByItem[l.itemId] || Number(item?.purchasePrice) || 0;
        return {
          itemId: l.itemId, itemCode: l.itemCode || item?.itemCode || '', itemName: l.itemName || item?.itemName || '',
          description: l.description || l.itemName || '', quantity: Number(l.quantity) || 0,
          unitOfMeasure: l.unitOfMeasure || 'Pcs', unitCost, unitPrice: unitCost,
          lineTotal: Math.round((Number(l.quantity) || 0) * unitCost * 100) / 100,
          trackingType: l.trackingType || 'None', batchNumber: l.batchNumber || '', serialNumbers: l.serialNumbers || [],
          lineNumber: i + 1
        };
      });
      const totalValue = Math.round(calculatedLines.reduce((s, l) => s + l.lineTotal, 0) * 100) / 100;
      const receivedQty = calculatedLines.reduce((s, l) => s + (Number(l.quantity) || 0), 0);

      const data = {
        docDate,
        parentId: send.id, parentDocNumber: send.docNumber, parentTranType: 'SND',
        // destination is locked to what the sender chose
        toBranchId: send.toBranchId || null, toBranchName: send.toBranchName || null,
        toLocationId: send.toLocationId || null, toLocationName: send.toLocationName || null,
        fromBranchName: send.fromBranchName || null, fromLocationName: send.fromLocationName || null,
        locationId: send.toLocationId || null, locationName: send.toLocationName || null,
        notes, status: 'Received', totalValue, lines: calculatedLines, receivedQty,
        financialYearId: activeFinancialYear.id,
        branchId: send.toBranchId || activeBranch?.id || null,
        branchName: send.toBranchName || activeBranch?.branchName || null
      };

      // Post the IN leg — single leg at (destination branch, destination location).
      const stockLines = calculatedLines.map(l => ({
        ...l, direction: 'in',
        locationId: send.toLocationId || null, locationName: send.toLocationName || '',
        branchId: send.toBranchId || null, branchName: send.toBranchName || null
      }));

      // Update the Send: cumulative received qty → Partially Received / Received.
      const sentQty = (send.lines || []).reduce((s, l) => s + (Number(l.quantity) || 0), 0);
      const newReceived = (Number(send.receivedQty) || 0) + receivedQty;
      const newStatus = newReceived >= sentQty ? 'Received' : 'Partially Received';

      // INV-15: the receive, its stock rows, the items' running averages and the
      // Send's received qty / status are ONE save (were three) — a dropped
      // connection can no longer leave a receive with no stock, or a Send that
      // does not know it was received. The id is fixed up front, so a retry
      // never creates a second receive.
      const addRows = async (w, ctx) => {
        const rows = await generateStockFlow(activeCompany.id, {
          transactionId: ctx.transactionId, tranTypeCode: TRAN_TYPE_CODE, docNumber: ctx.docNumber,
          docDate, financialYearId: activeFinancialYear.id,
          branchId: send.toBranchId || null, branchName: send.toBranchName || null,
          status: 'Received', locationId: send.toLocationId || null, locationName: send.toLocationName || '', lines: stockLines
        }, currentUser.uid, { writer: w });
        try {
          await costRowsChange(activeCompany.id, w, { added: rows, financialYearId: activeFinancialYear.id });
        } catch (e) {
          console.warn('[stock] item average not updated (Data Repair will rebuild it):', e && e.message);
        }
        w.update(doc(db, 'companies', activeCompany.id, 'transactions', send.id), {
          receivedQty: newReceived, status: newStatus, updatedAt: new Date().toISOString(), updatedBy: currentUser.uid
        });
      };
      if (!newDocRef.current) newDocRef.current = { id: doc(collection(db, 'companies', activeCompany.id, 'transactions')).id, tried: false };
      const pend = newDocRef.current;
      const earlier = await savedEarlier(activeCompany.id, pend);
      if (earlier && earlier.status !== 'Received') {
        // The earlier attempt was saved, but as something else (e.g. a Draft)
        // — don't pretend this one went through; let the user open it.
        alert(`${earlier.docNumber} was already saved as ${earlier.status} (the connection dropped before the confirmation arrived). Open it from the list to continue.`);
        setViewMode('list'); resetForm();
        loadRecords().catch(err => console.error('Background refresh failed:', err));
        return;
      }
      if (earlier) alert(`${earlier.docNumber} was already saved (the connection dropped before the confirmation arrived) — it was not saved again.`);
      const newTranData = earlier || await saveWithRows(
        (opts) => createTransaction(activeCompany.id, { ...data, tranTypeCode: TRAN_TYPE_CODE }, currentUser.uid, { ...opts, docId: pend.id }),
        addRows
      );

      applyLocal(prev => [{ ...newTranData, status: 'Received' }, ...prev]);
      invalidateItemStock(activeCompany.id);
      setViewMode('list'); resetForm();
      loadRecords().catch(err => console.error('Background refresh failed:', err));
    } catch (error) { console.error('Error:', error); alert('Error saving: ' + error.message); } finally { setSaving(false); }
  };

  const handleDelete = async (rec) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}? This returns the received stock.`)) return;
    try {
      // ACC-04 fix (2026-09-30): deleteTransaction() runs the safety checks FIRST
      // (linked documents, receipts, FBR) and only then removes ledger + stock rows.
      // Wiping them here beforehand left a blocked document with no accounting.
      await deleteTransaction(activeCompany.id, rec.id);
      if (rec.status === 'Received') {
        // roll back the Send's received qty (only after the delete succeeded)
        if (rec.parentId) {
          try {
            const parentSnap = await getDocs(query(collection(db, 'companies', activeCompany.id, 'transactions'), where('docNumber', '==', rec.parentDocNumber)));
            const parent = parentSnap.docs.map(d => ({ id: d.id, ...d.data() })).find(d => d.id === rec.parentId);
            if (parent) {
              const sentQty = (parent.lines || []).reduce((s, l) => s + (Number(l.quantity) || 0), 0);
              const newReceived = Math.max(0, (Number(parent.receivedQty) || 0) - (Number(rec.receivedQty) || 0));
              await updateTransaction(activeCompany.id, parent.id, { receivedQty: newReceived, status: newReceived <= 0 ? 'Sent' : (newReceived >= sentQty ? 'Received' : 'Partially Received') }, currentUser.uid);
            }
          } catch (e) { console.error('rollback parent failed', e); }
        }
      }
      invalidateItemStock(activeCompany.id); await loadRecords();
    } catch (error) { console.error('Error:', error); alert('Error deleting: ' + error.message); }
  };

  const anySerial = lineItems.some(l => l.trackingType === 'Serial');
  const anyBatch = lineItems.some(l => l.trackingType === 'Batch');

  const renderListView = () => (
    <>
      <ActionBar><SearchBox><FiSearch /><input type="text" placeholder="Search receive or transfer no..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} /></SearchBox>
        <FilterGroup><StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="All">All Status</option><option value="Received">Received</option><option value="Cancelled">Cancelled</option>
        </StatusFilterSelect>{canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Receive</TranAddButton>}</FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Receive Inventory documents</h3><p>{records.length === 0 ? 'Receive against a Send Inventory number' : 'No records match filters'}</p>
          {records.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Receive</TranAddButton>}</TranEmptyState>
      ) : (
        <TableContainer><Table><thead><tr><Th>Receive No</Th><Th>Date</Th><Th>Transfer No</Th><Th>Received At</Th><Th style={{ textAlign: 'right' }}>Qty</Th><Th style={{ textAlign: 'center' }}>Status</Th><Th style={{ textAlign: 'center' }}>Actions</Th></tr></thead><tbody>
          {filteredRecords.map(rec => (
            <Tr key={rec.id}><Td><strong>{rec.docNumber}</strong></Td><Td>{rec.docDate}</Td><Td>{rec.parentDocNumber || '-'}</Td>
              <Td>{`${rec.toBranchName ? rec.toBranchName + ' / ' : ''}${rec.toLocationName || '-'}`}</Td>
              <Td style={{ textAlign: 'right' }}>{rec.receivedQty ?? (rec.lines || []).reduce((s, l) => s + (Number(l.quantity) || 0), 0)}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                {canDelete && rec.status !== 'Cancelled' && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
              </ActionGroup></Td>
            </Tr>
          ))}</tbody></Table></TableContainer>
      )}
      <DocListPagination {...pagination} />
      <SummaryBar><span>Total: <strong>{listFiltering ? filteredRecords.length : pagination.totalCount}</strong></span><span>Received: <strong>{listSummary.counts.Received || 0}</strong></span></SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingRecord) return null; const rec = viewingRecord;
    return (<>
      <FormHeader><BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back</BackButton>
        <FormTitle>{rec.docNumber}<StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 12 }}>{rec.status}</StatusBadge></FormTitle>
      </FormHeader>
      <FormCard><SectionTitle>Receive Details</SectionTitle><InfoGrid>
        <InfoItem><label>Receive No</label><span>{rec.docNumber}</span></InfoItem><InfoItem><label>Date</label><span>{rec.docDate}</span></InfoItem>
        <InfoItem><label>Against Transfer No</label><span>{rec.parentDocNumber || '-'}</span></InfoItem>
        <InfoItem><label>Received At</label><span>{`${rec.toBranchName ? rec.toBranchName + ' / ' : ''}${rec.toLocationName || '-'}`}</span></InfoItem>
      </InfoGrid></FormCard>
      <FormCard><SectionTitle>Received Items</SectionTitle><TableContainer><Table><thead><tr><Th>#</Th><Th>Item</Th><Th style={{ textAlign: 'right' }}>Qty</Th><Th>Batch</Th><Th>Serials</Th></tr></thead><tbody>
        {(rec.lines || []).map((line, i) => (<Tr key={i}><Td>{i + 1}</Td><Td>{line.itemName || line.description}</Td><Td style={{ textAlign: 'right' }}>{line.quantity}{line.unitOfMeasure ? ` ${line.unitOfMeasure}` : ''}</Td><Td>{line.batchNumber || '-'}</Td><Td>{(line.serialNumbers || []).join(', ') || '-'}</Td></Tr>))}
      </tbody></Table></TableContainer></FormCard>
      {rec.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{rec.notes}</NotesText></FormCard>}
    </>);
  };

  const renderFormView = () => (
    <>
      <FormHeader><BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back</BackButton>
        <FormTitle>New Receive Inventory</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && send && <SaveButton $approve onClick={handleSave} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Receive (add to stock)'}</SaveButton>}
        </FormActions>
      </FormHeader>

      <FormCard><SectionTitle>Transfer Number</SectionTitle>
        <FormGrid>
          <FormGroup><Label>Date *</Label><Input type="date" value={docDate} onChange={e => setDocDate(e.target.value)} /></FormGroup>
          <FormGroup $span2><Label>Transfer No (from the sender) *</Label>
            <div style={{ display: 'flex', gap: 8 }}>
              <Input value={transferNo} onChange={e => setTransferNo(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupSend(); } }} placeholder="e.g. SND-LHR-2526-00001" disabled={!!send} />
              {!send ? <SaveButton onClick={lookupSend} disabled={looking}><FiSearch /> {looking ? 'Finding...' : 'Find'}</SaveButton>
                     : <SaveButton $secondary onClick={() => { setSend(null); setLineItems([]); setScanMsg(''); }}><FiX /> Change</SaveButton>}
            </div>
          </FormGroup>
        </FormGrid>
        {lookupErr && <div style={{ color: colors.error.main, fontSize: 13, marginTop: 8 }}>{lookupErr}</div>}
        {send && (
          <InfoGrid style={{ marginTop: 12 }}>
            <InfoItem><label>Receiving At (locked)</label><span>{`${send.toBranchName ? send.toBranchName + ' / ' : ''}${send.toLocationName || '-'}`}</span></InfoItem>
            <InfoItem><label>Sent On</label><span>{send.docDate}</span></InfoItem>
            <InfoItem><label>Status</label><span>{send.status}</span></InfoItem>
          </InfoGrid>
        )}
      </FormCard>

      {send && (
        <FormCard>
          <LineItemsHeader><SectionTitle style={{ margin: 0 }}><FiPackage style={{ verticalAlign: 'middle', marginRight: 6 }} />Scan / Add Received Items</SectionTitle>
            <SaveButton $secondary onClick={() => setShowCamera(true)}><FiCamera /> Camera Scan</SaveButton>
          </LineItemsHeader>
          <FormGrid>
            <FormGroup $span2><Label>Scan barcode (USB scanner works automatically; or type + Enter)</Label>
              <Input ref={scanInputRef} value={scanValue} onChange={e => setScanValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addByBarcode(scanValue); setScanValue(''); } }}
                placeholder="Scan or type a barcode / item code, then Enter" />
            </FormGroup>
            <FormGroup><Label>Add manually</Label>
              <ItemSelect items={items} selectedItemId="" onChange={(itemId) => { const it = items.find(i => i.id === itemId); if (it) addItem(it, 1); }} />
            </FormGroup>
          </FormGrid>
          {scanMsg && <div style={{ fontSize: 12, color: colors.text.secondary, marginBottom: 8 }}>{scanMsg}</div>}

          <LineTableContainer><LineTable><thead><tr>
            <LineTh style={{ width: 50 }}>#</LineTh><LineTh style={{ width: 240 }}>Item</LineTh><LineTh style={{ width: 130 }}>Qty</LineTh>
            {anyBatch && <LineTh style={{ width: 160 }}>Batch (read off goods)</LineTh>}
            {anySerial && <LineTh style={{ width: 280 }}>Serials (scan each)</LineTh>}
            <LineTh style={{ width: 50 }}></LineTh></tr></thead><tbody>
            {lineItems.length === 0 ? (
              <tr><LineTd colSpan={anyBatch || anySerial ? 6 : 4} style={{ textAlign: 'center', color: colors.text.tertiary, padding: 24 }}>Nothing scanned yet.</LineTd></tr>
            ) : lineItems.map((line, index) => (
              <tr key={index}>
                <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                <LineTd>{line.itemName}{line.trackingType !== 'None' ? <span style={{ fontSize: 11, color: colors.text.tertiary }}> · {line.trackingType}</span> : ''}</LineTd>
                <LineTd><LineInput type="number" min="0" value={line.quantity} onChange={e => setLineQty(index, e.target.value)} disabled={line.trackingType === 'Serial'} style={{ textAlign: 'right' }} /></LineTd>
                {anyBatch && <LineTd>{line.trackingType === 'Batch' ? <LineInput value={line.batchNumber} onChange={e => setLineBatch(index, e.target.value)} placeholder="Batch no." /> : <span style={{ color: '#999' }}>-</span>}</LineTd>}
                {anySerial && <LineTd>{line.trackingType === 'Serial' ? (
                  <div>
                    <LineInput placeholder="Scan/type serial + Enter" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSerial(index, e.target.value); e.target.value = ''; } }} />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                      {(line.serialNumbers || []).map(sn => (
                        <span key={sn} style={{ fontSize: 11, background: colors.neutral[100], borderRadius: 4, padding: '2px 6px' }}>{sn} <FiX size={10} style={{ cursor: 'pointer' }} onClick={() => removeSerial(index, sn)} /></span>
                      ))}
                    </div>
                  </div>
                ) : <span style={{ color: '#999' }}>-</span>}</LineTd>}
                <LineTd style={{ textAlign: 'center' }}><RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn></LineTd>
              </tr>
            ))}
          </tbody></LineTable></LineTableContainer>
        </FormCard>
      )}

      {send && <FormCard><SectionTitle>Notes</SectionTitle><FormGrid><FormGroup $span2><TextArea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any note about this receive (damage, shortage, etc.)" rows={2} /></FormGroup></FormGrid></FormCard>}

      {showCamera && <POSCameraScanner onDetected={(code) => { addByBarcode(code); return { ok: true, message: 'Added' }; }} onClose={() => setShowCamera(false)} />}
    </>
  );

  return (
    <PageLayout pageTitle="Receive Inventory" breadcrumb="Store / Receive Inventory">
      {viewMode === 'list' && renderListView()}{viewMode === 'form' && renderFormView()}{viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

export default ReceiveInventory;
